import type { onlineEn } from './online-en.ts';

type DeepString<T> = T extends string ? string : { [K in keyof T]: DeepString<T[K]> };

/** Italian strings of the online screens. Typed against the English shape. */
export const onlineIt: DeepString<typeof onlineEn> = {
  online: {
    title: 'Online',
    hub: {
      intro: 'Gioca con altri: entra in un tavolo, o raduna la tua banda in una stanza privata.',
      quick: 'Partita rapida',
      quickHint:
        'Un tavolo aperto qualsiasi, nella tua lingua. Dopo una breve attesa i bot riempiono i posti vuoti.',
      ranked: 'Classificata',
      rankedHint:
        'Quattro o cinque giocatori, niente bot, difficoltà Professionista. La tua valutazione si muove a ogni colpo.',
      rankedGuest:
        'La Classificata richiede un account vero: un ospite non può giocarla. Prima trasforma il tuo account ospite in uno vero.',
      create: 'Stanza privata',
      createHint: 'Scegli tu le regole e condividi un codice, un link o un QR con la tua banda.',
      join: 'Entra con un codice',
      codeLabel: 'Codice della stanza',
      codeHint: 'Sei caratteri, come K7M2QX.',
      joinAction: 'Entra',
      friends: 'Amici',
      friendsHint: 'Guarda chi c’è, invitalo in una stanza privata, chiedi l’amicizia a qualcuno.',
      friendsGuest: 'Gli amici richiedono un account vero: un ospite non può averne.',
      friendsOpen: 'Apri gli amici',
      back: 'Torna alla stanza',
      backHint: 'Sei ancora a un tavolo.',
      resume: 'Rientra al tuo tavolo',
      resumeHint:
        'Il tuo posto è ancora al tavolo di prima (una pagina ricaricata o un’app chiusa non lo lasciano). Rientra: da lì puoi anche lasciarlo.',
      signIn: 'Accedi per giocare online',
      signInBody:
        'Per giocare online serve un account (va bene anche da ospite). La partita contro i bot non ne ha bisogno.',
      signInAction: 'Apri l’account',
      consentBody:
        'Prima di giocare online accetta le Condizioni d’uso e l’Informativa sulla privacy nel tuo account.',
      unavailable: 'Il gioco online non è disponibile in questa versione',
      unavailableBody:
        'Questa versione non ha un servizio account o un server di gioco a cui collegarsi.',
      connecting: 'Collegamento al tavolo…',
      waking:
        'Il server dormiva e si sta svegliando. Può volerci fino a un minuto: resta su questa pagina.',
    },
    room: {
      title: 'Stanza',
      code: 'Codice della stanza',
      copy: 'Copia il codice',
      copyLink: 'Copia il link d’invito',
      copied: 'Copiato.',
      share: 'Condividi l’invito',
      shareTitle: 'Il Colpo',
      shareText: 'Entra nel mio tavolo a Il Colpo: il codice è {{code}}.',
      qr: 'Codice QR dell’invito',
      players: 'Giocatori',
      you: 'tu',
      host: 'Organizzatore',
      ready: 'Pronto',
      notReady: 'Non pronto',
      bot: 'Bot',
      dropped: 'Offline',
      kick: 'Rimuovi',
      start: 'Inizia il colpo',
      startWaiting: 'In attesa che tutti siano pronti.',
      startNeedsPlayers:
        'Per un colpo servono almeno tre al tavolo: accendi i bot o aspetta altri giocatori.',
      imReady: 'Sono pronto',
      imNotReady: 'Non pronto',
      leave: 'Lascia la stanza',
      quickWait: 'Il colpo inizia fra {{seconds}} s. I bot riempiranno i posti vuoti.',
      quickWaitNow: 'Si parte…',
      quickInfo: 'Partita rapida',
      rankedInfo: 'Classificata',
      rankedNeeded:
        'In attesa di giocatori (presenti: {{here}}): ne servono almeno quattro, niente bot.',
      rankedWait:
        'Il colpo inizia fra {{seconds}} s con chi c’è. Un quinto giocatore lo fa partire subito.',
      rankedRules:
        'Difficoltà Professionista, niente bot. Lasciare un colpo in Classificata costa un posto in coda.',
      privateInfo: 'Stanza privata',
      rules: 'Regole',
      map: 'Colpo',
      difficulty: 'Difficoltà',
      infiltrator: 'Il Talpa',
      size: 'Posti',
      sizeOption: '{{count}} posti',
      bots: 'I bot riempiono i posti vuoti',
      botsOn: 'Sì',
      botsOff: 'No',
      language: 'Vocabolario',
      draft: 'Scegli i ruoli',
      draftOn: 'Sì',
      draftOff: 'No',
      draftHint:
        'Quando l’organizzatore avvia, i giocatori vengono chiamati in ordine casuale e ognuno prende un ruolo ancora libero. I bot ricevono gli altri.',
      rulesReadOnly: 'Le regole le decide l’organizzatore.',
      kicked: 'Sei stato rimosso dalla stanza.',
      closed: 'La stanza è chiusa.',
      infiltratorOptions: { never: 'Spento', always: 'Sempre', normal: 'A sorte' },
      draftPanel: {
        title: 'Scegliete i ruoli',
        yourTurn:
          'Tocca a te: prendi un ruolo. Ancora {{seconds}} s, poi ne viene scelto uno per te.',
        turnOf: 'Sta scegliendo {{name}} ({{seconds}} s).',
        starting: 'Tutti hanno un ruolo. Si parte…',
        order: 'Ordine delle scelte',
        free: 'Ruoli ancora liberi',
        pick: 'Prendi {{role}}',
        waiting: 'In attesa',
        picked: 'Scelto',
      },
    },
    chat: {
      title: 'Chiacchiere al tavolo',
      open: 'Chat',
      close: 'Chiudi',
      placeholder: 'Di’ qualcosa al tavolo',
      send: 'Invia',
      empty: 'Nessuno ha ancora detto nulla.',
      emotes: 'Emote',
      emoteSent: '{{name}}: {{emote}}',
      emote: {
        hat: 'Un tocco di cappello',
        thumbsup: 'Pollice in su',
        shh: 'Silenzio',
        sweat: 'Sudori freddi',
        cigar: 'Sigaro',
        watch: 'Orologio da tasca',
        glass: 'Calice alzato',
        magnifier: 'Lente d’ingrandimento',
      },
      forbidden:
        'Il testo libero è per i giocatori dai 14 anni in su. Le frasi rapide sono sempre disponibili.',
      you: 'Tu',
    },
    game: {
      reconnecting: 'Collegamento perso. Riconnessione…',
      leaveBody:
        'Un bot giocherà il tuo posto per sempre, e gli altri andranno avanti senza di te.',
      lostTitle: 'Il collegamento è perso',
      lostBody: 'Non siamo riusciti a riportarti al tavolo.',
      backToOnline: 'Torna a online',
    },
    errors: {
      protocolMismatch:
        'Questa versione del gioco è troppo vecchia per giocare online. Aggiorna l’app.',
      unauthorized: 'Non hai fatto l’accesso, o la sessione è scaduta. Accedi di nuovo.',
      roomFull: 'Questo tavolo è pieno.',
      notFound: 'Non c’è nessuna stanza con questo codice.',
      gameInProgress: 'Il colpo è già iniziato.',
      alreadyInRoom: 'Sei già a un altro tavolo.',
      queueBanned:
        'Hai lasciato un colpo in Classificata da poco. Potrai rimetterti in coda fra {{minutes}} min.',
      accountRequired: 'La Classificata richiede un account vero, non un ospite.',
      featureOff: 'Per ora è spento. Riprova più tardi.',
      noAttempts: 'Hai usato i tre tentativi del colpo di oggi. Torna domani.',
      banned: 'Il tuo account è sospeso dal gioco online.',
      bannedMinutes: 'Il tuo account è sospeso dal gioco online. Finisce fra {{count}} min.',
      bannedHours: 'Il tuo account è sospeso dal gioco online. Finisce fra circa {{count}} h.',
      bannedDays: 'Il tuo account è sospeso dal gioco online. Finisce fra circa {{count}} g.',
      muted: 'Per ora la chat ti è chiusa. Le frasi rapide funzionano ancora.',
      unavailable: 'Il servizio non è disponibile al momento. Riprova tra poco.',
      updateRequired:
        'Questa versione del gioco è troppo vecchia per giocare online. Aggiorna l’app.',
      notYourTurn: 'Non è il tuo turno di scegliere.',
      taken: 'Qualcuno ha già preso quel ruolo.',
      notHost: 'Solo l’organizzatore può farlo.',
      badRequest: 'Non ha funzionato.',
      rateLimited: 'Piano, un po’ più piano.',
      illegal: 'Ora non puoi farlo.',
      chatForbidden: 'Il testo libero è per i giocatori dai 14 anni in su.',
      notEnoughPlayers: 'Non ci sono abbastanza giocatori al tavolo.',
      consentRequired:
        'Prima accetta Condizioni d’uso e Informativa sulla privacy nel tuo account.',
      connectionLost: 'Il collegamento è perso.',
      unreachable: 'Il server di gioco non risponde. Riprova fra un momento.',
    },
  },
};
